import { useFormik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { AddDailyCreditsInput } from '../../../domain/dtos/add-daily-credits-input'
import { PlanType } from '../../../domain/enums/plan-type'
import { useAddDailyCredits } from '../../hooks/use-add-daily-credits'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@hub-la/shadcn'

import { PlusSquareIcon } from 'lucide-react'

type Props = {
  id: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
  planType: PlanType
  remainingCredits: number
}

export type Values = Omit<AddDailyCreditsInput, 'id'>

export const AddDailyCreditsModal: React.FC<Props> = (props) => {
  const { id, open, onClose, onSuccess, planType, remainingCredits } = props
  const { mutateAsync: extendSubscription, isLoading } = useAddDailyCredits()
  const { t } = useTranslation()
  const { toast } = useToast()

  const formik = useFormik<Values>({
    validationSchema: Yup.object().shape({
      credits: Yup.number().required('errors.required'),
    }),
    validateOnMount: false,
    validateOnChange: true,
    enableReinitialize: true,
    initialValues: {
      credits: undefined,
    },
    onSubmit: async (values: Values) => {
      await extendSubscription(
        { id, credits: values.credits },
        {
          onSuccess: () => {
            onSuccess()
            onClose()
            toast({
              description: t('success'),
            })
          },
          onError: (error) => {
            toast({
              description: t((error as Error).message ?? ''),
              variant: 'destructive',
            })
          },
        },
      )
    },
  })

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Estender vencimento</DialogTitle>
          <DialogDescription>
            Ao estender a data de vencimento de assinaturas ativas, os dias adicionais serão incorporados à data
            original de vencimento. Para assinaturas canceladas, a nova data de vencimento será atualizada, mantendo o
            status como cancelado. As assinaturas inativas não serão afetadas por esta ação.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
              <PlusSquareIcon className="h-4 w-4" />
            </div>
            <div>
              <p className="font-medium">{t(`planType.${planType}`)}</p>
              <p className="text-sm text-muted-foreground">{remainingCredits} dias restantes</p>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="credits">Dia(s)</Label>
            <Input
              id="credits"
              type="number"
              value={formik.values.credits}
              onChange={(e) => formik.setFieldValue('credits', parseInt(e.target.value, 10))}
              onBlur={formik.handleBlur}
            />
            {formik.touched.credits && formik.errors.credits && (
              <p className="text-sm text-destructive">{t(formik.errors.credits)}</p>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={isLoading} onClick={() => formik.handleSubmit()} disabled={isLoading}>
            Estender vencimento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
